;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();const AUTH_REFRESH_CHANNEL = 'native-teams-auth-refresh';
const AUTH_REFRESH_LOCK = 'native-teams-auth-refresh';
export const AUTH_REFRESH_COOKIE_KEY = 'auth_refresh_event';
export const AUTH_SESSION_EXPIRY_COOKIE_KEY = 'auth_session_expiry';
export const AUTH_SESSION_SYNC_HEADER = 'x-native-teams-session-sync';
export const AUTH_SESSION_SYNC_HEADER_VALUE = '1';
export const AUTH_REFRESH_BUFFER_MS = 60_000;

const AUTH_REFRESH_LAST_SEEN_KEY = 'auth_refresh_event_last_seen';
const CROSS_APP_CHECK_INTERVAL_MS = 30_000;
const REFRESH_PROMISE_HOLD_MS = 500;
const REFRESH_MARKER_POLL_INTERVAL_MS = 100;
const REFRESH_MARKER_WAIT_TIMEOUT_MS = 5_000;

export interface AuthRefreshResult {
  success: boolean;
  isProcessing?: boolean;
  data?: {
    access_token: string;
  };
}

interface AuthRefreshMessage {
  type: 'access-token-refreshed';
  accessToken: string;
}

let refreshPromise: Promise<AuthRefreshResult> | null = null;

export function createAuthRefreshMarkerValue(): string {
  return `${Date.now()}:${globalThis.crypto.randomUUID()}`;
}

export function createAuthSessionExpiryValue(expiresInSeconds: number): string {
  const issuedAt = Date.now();
  return `${issuedAt}:${issuedAt + expiresInSeconds * 1000}`;
}

function readCookieValue(name: string): string | null {
  if (typeof document === 'undefined') return null;

  const cookie = document.cookie
    .split('; ')
    .find((cookiePart) => cookiePart.startsWith(`${name}=`));

  if (!cookie) return null;

  return decodeURIComponent(cookie.slice(name.length + 1));
}

export function getAuthRefreshMarker(): string | null {
  return readCookieValue(AUTH_REFRESH_COOKIE_KEY);
}

export function waitForAuthRefreshMarkerChange(
  previousMarker: string | null,
  timeoutMs = REFRESH_MARKER_WAIT_TIMEOUT_MS
): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (getAuthRefreshMarker() !== previousMarker) return Promise.resolve(true);

  return new Promise((resolve) => {
    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      const hasChanged = getAuthRefreshMarker() !== previousMarker;
      const hasTimedOut = Date.now() - startedAt >= timeoutMs;
      if (!hasChanged && !hasTimedOut) return;

      window.clearInterval(intervalId);
      resolve(hasChanged);
    }, REFRESH_MARKER_POLL_INTERVAL_MS);
  });
}

function markCurrentAuthRefreshSeen(): void {
  if (typeof window === 'undefined') return;

  const marker = readCookieValue(AUTH_REFRESH_COOKIE_KEY);
  if (marker) window.sessionStorage.setItem(AUTH_REFRESH_LAST_SEEN_KEY, marker);
}

function createRefreshChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined' || !('BroadcastChannel' in window)) return null;

  return new BroadcastChannel(AUTH_REFRESH_CHANNEL);
}

function isValidRefreshMessage(
  message: AuthRefreshMessage | null | undefined
): message is AuthRefreshMessage {
  return message?.type === 'access-token-refreshed' && typeof message.accessToken === 'string';
}

async function runWithRefreshLock(
  refresh: () => Promise<AuthRefreshResult>
): Promise<AuthRefreshResult> {
  if (typeof navigator === 'undefined' || !('locks' in navigator)) return refresh();

  return navigator.locks.request(AUTH_REFRESH_LOCK, refresh);
}

export function subscribeToAuthTokenRefresh(
  onAccessToken: (accessToken: string) => void
): () => void {
  const channel = createRefreshChannel();
  if (!channel) return () => undefined;

  const handleMessage = (event: MessageEvent<AuthRefreshMessage>) => {
    if (!isValidRefreshMessage(event.data)) return;
    markCurrentAuthRefreshSeen();
    onAccessToken(event.data.accessToken);
  };

  channel.addEventListener('message', handleMessage);

  return () => {
    channel.removeEventListener('message', handleMessage);
    channel.close();
  };
}

export function subscribeToCrossAppAuthRefresh(callback: () => Promise<boolean>): () => void {
  if (typeof window === 'undefined') return () => undefined;

  markCurrentAuthRefreshSeen();
  let isSynchronizing = false;

  const checkMarker = () => {
    const marker = readCookieValue(AUTH_REFRESH_COOKIE_KEY);
    if (!marker) return;
    if (marker === window.sessionStorage.getItem(AUTH_REFRESH_LAST_SEEN_KEY)) return;
    if (isSynchronizing) return;

    isSynchronizing = true;
    void callback()
      .then((didSynchronize) => {
        if (didSynchronize) {
          window.sessionStorage.setItem(AUTH_REFRESH_LAST_SEEN_KEY, marker);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        isSynchronizing = false;
      });
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return;
    checkMarker();
  };

  window.addEventListener('focus', checkMarker);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  const intervalId = window.setInterval(checkMarker, CROSS_APP_CHECK_INTERVAL_MS);

  return () => {
    window.removeEventListener('focus', checkMarker);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.clearInterval(intervalId);
  };
}

export function coordinateAuthTokenRefresh(
  performRefresh: () => Promise<AuthRefreshResult>,
  onAccessToken: (accessToken: string) => void
): Promise<AuthRefreshResult> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const channel = createRefreshChannel();
    let broadcastAccessToken: string | undefined;

    const handleMessage = (event: MessageEvent<AuthRefreshMessage>) => {
      if (!isValidRefreshMessage(event.data)) return;
      broadcastAccessToken = event.data.accessToken;
    };

    channel?.addEventListener('message', handleMessage);

    try {
      return await runWithRefreshLock(async () => {
        if (broadcastAccessToken) {
          markCurrentAuthRefreshSeen();
          onAccessToken(broadcastAccessToken);
          return {
            success: true,
            data: { access_token: broadcastAccessToken },
          };
        }

        const result = await performRefresh();
        const accessToken = result.data?.access_token;
        if (!result.success || !accessToken) return result;

        markCurrentAuthRefreshSeen();
        onAccessToken(accessToken);
        channel?.postMessage({
          type: 'access-token-refreshed',
          accessToken,
        } satisfies AuthRefreshMessage);

        return result;
      });
    } finally {
      channel?.removeEventListener('message', handleMessage);
      channel?.close();

      setTimeout(() => {
        refreshPromise = null;
      }, REFRESH_PROMISE_HOLD_MS);
    }
  })();

  return refreshPromise;
}
